/* eslint-disable jsdoc/require-param */
/* eslint-disable jsdoc/check-param-names */
import React from "react";
import { makeStyles } from "@material-ui/styles";
import { Tabs, Tab, Box } from "@material-ui/core";

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: "#FFFFFF",
    borderRadius: "4px",
    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
  },
  tabs: {
    "& .MuiTabs-indicator": {
      height: 3,
      backgroundColor: theme.palette.primary.main,
    },
  },
  tab: {
    textTransform: "none",
    fontWeight: 500,
    fontSize: "1rem",
    padding: theme.spacing(2),
    "&.Mui-selected": {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
}));

/**
 * Renders a tabbed navigation component for training modules.
 * The component displays tabs for "All Modules", "Required", "In Progress", and "Completed".
 * Each tab is styled and highlights when selected.
 *
 * @param {number} activeTab - The index of the currently active tab.
 * @param {Function} onChange - Callback function to handle tab change events.
 */
function TabbedNavigation({ activeTab, onChange }) {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => onChange(newValue)}
        indicatorColor="primary"
        className={classes.tabs}
        textColor="primary"
        variant="fullWidth"
      >
        <Tab label="All Modules" className={classes.tab} />
        <Tab label="Required" className={classes.tab} />
        <Tab label="Completed" className={classes.tab} />
      </Tabs>
    </div>
  );
}

/**
 * Component to render a tab panel.
 * The component uses the MUI `Tabs` component to render a tab panel.
 * The component renders a `div` element with the `role` attribute set to `"tabpanel"`,
 * and the `hidden` attribute set to `true` if the `value` prop does not match the `index` prop.
 * The component also renders a nested `Box` element with the content of the tab panel.
 * The component uses the `id` and `aria-labelledby` attributes to associate the tab panel
 * with its corresponding tab in the tab bar.
 *
 * Props:
 * - `children`: The content of the tab panel.
 * - `value`: The currently active tab index.
 * - `index`: The index of this tab panel.
 * - `other`: Any additional props that should be passed to the `div` element.
 *
 * @param {{children: React.ReactNode, value: number, index: number, [key: string]: any}} props
 * The props for the component.
 * @returns {React.ReactElement}
 * The rendered component.
 */
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box p={0}>{children}</Box>}
    </div>
  );
}

export { TabbedNavigation, TabPanel };
